/**
 * District API Contract
 *
 * Districts are geographic groupings of locations with district managers.
 * Behind the scenes, districts use the tagging system for location assignment.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateDistrictInputSchema, UpdateDistrictInputSchema, BatchAddLocationsInputSchema, DistrictResponseSchema, DistrictDetailResponseSchema, UnassignedLocationsResponseSchema, BatchAddLocationsResponseSchema, DistrictsWithLocationsResponseSchema, } from "@hourly/api/district";
import { uuidSchema } from "@hourly/api/validation-common";
// ============================================
// District CRUD Contracts
// ============================================
/**
 * POST /districts
 * Create a new district
 */
export const createDistrict = oc
    .route({
    method: "POST",
    path: "/districts",
    summary: "Create a new district",
    tags: ["Districts"],
    successStatus: 201,
})
    .input(CreateDistrictInputSchema)
    .output(DistrictDetailResponseSchema);
/**
 * GET /districts
 * List all districts for a company
 */
export const listDistricts = oc
    .route({
    method: "GET",
    path: "/districts",
    summary: "List districts for a company",
    tags: ["Districts"],
})
    .input(z.object({}))
    .output(z.array(DistrictResponseSchema));
/**
 * GET /districts/{districtId}
 * Get a district by ID with full details
 */
export const getDistrict = oc
    .route({
    method: "GET",
    path: "/districts/{districtId}",
    summary: "Get district by ID with locations and managers",
    tags: ["Districts"],
})
    .input(z.object({ districtId: uuidSchema }))
    .output(DistrictDetailResponseSchema);
/**
 * PUT /districts/{districtId}
 * Update a district
 */
export const updateDistrict = oc
    .route({
    method: "PUT",
    path: "/districts/{districtId}",
    summary: "Update a district",
    tags: ["Districts"],
})
    .input(z.object({ districtId: uuidSchema }).merge(UpdateDistrictInputSchema))
    .output(DistrictDetailResponseSchema);
/**
 * DELETE /districts/{districtId}
 * Delete a district
 */
export const deleteDistrict = oc
    .route({
    method: "DELETE",
    path: "/districts/{districtId}",
    summary: "Delete a district",
    tags: ["Districts"],
    successStatus: 204,
})
    .input(z.object({ districtId: uuidSchema }))
    .output(z.void());
// ============================================
// District Location Management Contracts
// ============================================
/**
 * POST /districts/{districtId}/locations/{locationId}
 * Add a location to a district
 */
export const addLocationToDistrict = oc
    .route({
    method: "POST",
    path: "/districts/{districtId}/locations/{locationId}",
    summary: "Add a location to a district",
    tags: ["Districts"],
    successStatus: 201,
})
    .input(z.object({ districtId: uuidSchema, locationId: uuidSchema }))
    .output(DistrictDetailResponseSchema);
/**
 * POST /districts/{districtId}/locations
 * Batch add multiple locations to a district
 */
export const batchAddLocationsToDistrict = oc
    .route({
    method: "POST",
    path: "/districts/{districtId}/locations",
    summary: "Batch add multiple locations to a district",
    tags: ["Districts"],
    successStatus: 201,
})
    .input(z.object({ districtId: uuidSchema }).merge(BatchAddLocationsInputSchema))
    .output(BatchAddLocationsResponseSchema);
/**
 * DELETE /districts/{districtId}/locations/{locationId}
 * Remove a location from a district
 */
export const removeLocationFromDistrict = oc
    .route({
    method: "DELETE",
    path: "/districts/{districtId}/locations/{locationId}",
    summary: "Remove a location from a district",
    tags: ["Districts"],
})
    .input(z.object({ districtId: uuidSchema, locationId: uuidSchema }))
    .output(DistrictDetailResponseSchema);
/**
 * GET /districts/unassigned-locations
 * Get locations not assigned to any district
 */
export const getUnassignedLocations = oc
    .route({
    method: "GET",
    path: "/districts/unassigned-locations",
    summary: "Get locations not assigned to any district",
    tags: ["Districts"],
})
    .input(z.object({}))
    .output(UnassignedLocationsResponseSchema);
/**
 * GET /districts/with-locations
 * List all districts with their location IDs (bulk, no N+1)
 */
export const listDistrictsWithLocations = oc
    .route({
    method: "GET",
    path: "/districts/with-locations",
    summary: "List all districts with their location IDs (bulk, no N+1)",
    tags: ["Districts"],
})
    .input(z.object({}))
    .output(DistrictsWithLocationsResponseSchema);
// ============================================
// District Manager Contracts
// ============================================
/**
 * POST /districts/{districtId}/managers/{employmentId}
 * Add a manager to a district
 */
export const addDistrictManager = oc
    .route({
    method: "POST",
    path: "/districts/{districtId}/managers/{employmentId}",
    summary: "Add a manager to a district",
    tags: ["Districts"],
    successStatus: 201,
})
    .input(z.object({ districtId: uuidSchema, employmentId: uuidSchema }))
    .output(DistrictDetailResponseSchema);
/**
 * DELETE /districts/{districtId}/managers/{employmentId}
 * Remove a manager from a district
 */
export const removeDistrictManager = oc
    .route({
    method: "DELETE",
    path: "/districts/{districtId}/managers/{employmentId}",
    summary: "Remove a manager from a district",
    tags: ["Districts"],
})
    .input(z.object({ districtId: uuidSchema, employmentId: uuidSchema }))
    .output(DistrictDetailResponseSchema);
// ============================================
// Contract Router
// ============================================
export const districtContract = {
    // CRUD operations
    create: createDistrict,
    list: listDistricts,
    get: getDistrict,
    update: updateDistrict,
    delete: deleteDistrict,
    // Location management
    addLocation: addLocationToDistrict,
    batchAddLocations: batchAddLocationsToDistrict,
    removeLocation: removeLocationFromDistrict,
    getUnassignedLocations,
    listWithLocations: listDistrictsWithLocations,
    // Manager management
    addManager: addDistrictManager,
    removeManager: removeDistrictManager,
};
//# sourceMappingURL=district.js.map